import { PaymentType } from '@/models/payment-plan';
import { DurationOption } from '@/core/domain/entities/Product';

export const Environment = {
  currency: 'GHS',
  products: 'inventory',
  partners: 'merchant_shops',
  platform: 'ecommerce',
  merchantCategory: 'product_category',
  inventoryCategory: 'product_category',
  noImage: '/assets/images/no-image.png',
  origin: 'Motito Marketplace',
  verificationCardCollection: 'identification_card',
  terms: 'https://motito.co/terms-and-conditions.html',
  privacy: 'https://motito.co/privacy-policy.html',
  daysInMonth: 30,
  frequency: {
    daily: 1,
    monthly: 30,
    weekly: 7,
  } as Record<'daily' | 'monthly' | 'weekly', number>,
  orders: 'orders',

  baseBillingFrequency: (key: keyof DurationOption) => {
    switch (key) {
      case 'monthly':
        return 'Months';
      case 'weekly':
        return 'Weeks';
      case 'daily':
        return 'Days';
      default:
        return '';
    }
  },
  category: [
    {
      title: 'Phones & Accessories',
      value: 'mobile_phones',
      query: [],
    },
    {
      title: 'Computing',
      value: 'computing',
      query: [],
    },
    {
      title: 'Electronics',
      value: 'electronics',
      query: [],
    },
    {
      title: 'Furniture',
      value: 'furniture',
      query: [],
    },
    {
      title: 'Beauty & Fashion',
      value: 'beauty_fashion',
      query: [],
    },
    {
      title: 'Appliances',
      value: 'electronics',
      query: [],
    },
    {
      title: 'Industrial Machinery',
      value: 'industrial_machinery',
      query: [],
    },
  ],
  gender: ['Male', 'Female'],
  status: ['Married', 'Single', 'Divorced'],
  identities: [
    {
      assets: '/assets/svgs/Icons/IdentificationCard.svg',
      title: 'Ghana card',
      url: 'ghana_card',
      description: 'Upload a photo or scan of your Ghana card',
      type: 'IDType.NationalID',
    },
    {
      assets: '/assets/svgs/Icons/IdentificationBadge.svg',
      title: 'Passport',
      url: 'passport',
      description: 'Upload a photo or scan of your passport photo page',
      type: 'IDType.NationalID',
    },
    // {
    //   assets: '/assets/svgs/Icons/IdentificationCard.svg',
    //   title: "Driver's License",
    //   url: 'driver_license',
    //   description: "Upload a photo or scan your driver's license",
    // },
    // {
    //   assets: '/assets/svgs/Icons/AddressBook.svg',
    //   title: "Voter's ID",
    //   url: 'voter_id',
    //   description: "Upload a photo or scan of your voter's ID card",
    // },
  ],
  regions: [
    'Ashanti Region',
    'Brong Ahafo Region',
    'Central Region',
    'Eastern Region',
    'Greater Accra Region',
    'Northern Region',
    'Upper East Region',
    'Upper West Region',
    'Volta Region',
    'Western Region',
    'Savannah Region',
    'Bono East Region',
    'Oti Region',
    'Ahafo Region',
    'Western North Region',
    'North East Region',
  ],
  /**
   * @deprecated Use goodToKnow instead for checkout messaging
   * This is kept for backwards compatibility only
   */
  paymentPlanInfo: [
    'Your payments are saved securely until pickup.',
    'Collect your product when you\'re done saving.',
    'First month of price lock is free.',
  ],

  /**
   * "Good to know" messaging shown on checkout page
   * 2 bullets that address key user anxieties
   */
  goodToKnow: [
    'Collect your item when you finish paying.',
    'Cancel anytime (cancellation fee applies).',
  ],

  /**
   * Key Terms shown in the bottom sheet before order creation
   * Conditional: KMP ON shows 2 bullets, KMP OFF shows 3 bullets
   */
  keyTerms: {
    version: '1.0.0',
    always: [
      'Your payments go toward this item only. This is not a savings account.',
      '10% cancellation fee applies if you cancel your plan.',
    ],
    kmpOff: [
      "Without Keep My Price, you'll pay more if the product price goes up.",
    ],
  },
  paymentOptions: [
    {
      title: 'Pay Small Small',
      desc: 'Save up to 60% and receive item while you pay the rest.',
      learn: '/help',
      icon: '/assets/svgs/PiggyBank.svg',
      type: PaymentType.PAYSMALL_SMALL,
    },
    // Hidden for now - focusing on Pay Small Small only
    // {
    //   icon: '/assets/svgs/CardHolder.svg',
    //   title: 'Other payment options',
    //   desc: 'Explore our other flexible payment options.',
    //   learn: '/help',
    //   type: PaymentType.FULL_PAYMENT,
    // },
  ],
  subPaymentOptions: [
    {
      provider: 'MTN',
      title: 'MTN Pay Small Small',
      desc: 'Save with your MTN MoMo wallet',
      value: 'PaymentPartner.MTN_MOMO',
      type: 'mtn_mobile_money',
    },
    {
      provider: 'Telecel',
      title: 'Telecel Pay Small Small',
      desc: 'Save with your Telecel Cash wallet',
      value: 'PaymentPartner.VODAFONE_CASH',
      type: 'telecel_mobile_money',
      coming_soon: true,
    },
    {
      provider: 'Motito',
      title: 'Motito Pay Small Small',
      desc: 'Save with your Motito Ecobank Xpress account',
      value: 'x',
      type: 'debit_card',
    },
    {
      provider: 'Card',
      title: 'Buy now PayLater (subject to eligibility)',
      desc: 'Buy now, pay-later. Make a 40% deposit and pick your item',
      value: 'PaymentPartner.MTN_MOMO',
      type: PaymentType.INSTALLMENT,
    },
    {
      provider: 'Paystack',
      title: 'Pay now - Cash & Carry',
      desc: 'One time full purchase',
      value: 'PaymentPartner.ECOBANK',
      type: 'debit_card',
    },
  ],
  partnerships: {
    telecel: {
      logo: '/assets/images/partners/telecel.png',
      colorLight: '#FF868636',
      title: 'PaySmallSmall With Telecel.',
      description:
        'Conveniently save for your next big item while keeping the price locked ',
      primaryColor: '#E32527',
      showDownloadApp: false,
      textColor: '#141414',
      host: 'telecel.motito.co',
      // Banner shown on partner domains
      banner: {
        show: true,
        text: 'Motito has partnered with Telecel to provide financing options for Telecel customers.',
      },
      // Slider configuration for homepage
      slider: {
        backgroundColor: '#E32527',
        gradientEndColor: '#FF6B6B', // Red to lighter red gradient
        image: '/assets/images/telecel-slider-image.png',
        title: 'Pay Small Small',
        subtitle:
          "Phones, Fridges, Laptops and more.<br />Market prices change often. Yours won't.",
        description: 'Use Telecel Cash PaySmallSmall Now!',
        showButton: false,
      },
      /**
       * @deprecated Partner-specific goodToKnow no longer used - use global Environment.goodToKnow
       */
    },
    mtn: {
      logo: '/assets/logos/mtn.png',
      colorLight: '#004F71',
      title: 'PaySmallSmall With MTN MoMo.',
      description:
        'Conveniently save for your next big item while keeping the price locked ',
      primaryColor: '#141414',
      showDownloadApp: false,
      textColor: '#FFFFFF',
      host: 'mtn.motito.co',
      // Banner shown on partner domains
      banner: {
        show: true,
        text: 'Motito has partnered with MTN to provide financing options for MTN customers.',
      },
      // Slider configuration for homepage
      slider: {
        backgroundColor: '#004F71',
        gradientEndColor: '#CDDC39', // Blue to lime/yellow gradient (MTN brand)
        image: '/assets/images/mtn-slider-image.png',
        title: 'Pay Small Small',
        subtitle:
          "Phones, Fridges, Laptops and more.<br />Market prices change often. Yours won't.",
        description: 'Use MTN PaySmallSmall Now!',
        showButton: false,
      },
    },
    ecobank: {
      logo: '/assets/images/partners/ecobank.png',
      colorLight: '#E3DFFF',
      title: 'Buy Now. Pay later. With Ecobank.',
      description:
        'Enjoy unbeatable instalment payment plan as an Ecobank customer',
      primaryColor: '#0046C8',
      showDownloadApp: false,
      textColor: '#141414',
      host: 'ecobank.motito.co',
      // Banner shown on partner domains
      banner: {
        show: true,
        text: 'Motito has partnered with Ecobank to provide financing options for Ecobank customers.',
      },
      // Slider configuration for homepage
      slider: {
        backgroundColor: '#0046C8',
        gradientEndColor: '#00A8CC', // Blue to cyan gradient
        image: '/assets/images/partners/ecobank/laptop.png',
        title: 'Buy Now. Pay Later.',
        subtitle: 'Enjoy unbeatable instalment payment plans',
        description: 'Finance your next big purchase with Ecobank integration.',
        showButton: false,
      },
    },
    motito: {
      logo: '/assets/svgs/motito.svg',
      colorLight: '#E3DFFF',
      title: 'Buy Now. Pay later. With Motito.',
      description:
        'Enjoy unbeatable instalment payment plan with Motito',
      primaryColor: '#0041C1', // Motito blue
      showDownloadApp: true,
      textColor: '#141414',
      host: 'motito.co',
      // No banner for generic Motito marketplace
      banner: {
        show: false,
        text: '',
      },
      // Generic slider for Motito marketplace
      slider: {
        backgroundColor: '#0041C1', // Motito blue
        gradientEndColor: '#0041C1',
        image: '/assets/images/sliderImage.png',
        title: 'PaySmallSmall for your next phone, laptop, fridge and more.',
        subtitle: '',
        description: '',
        showButton: true,
      },
    },
  },
  appUrl: 'https://google.com',
  links: {
    tnc: '/signup/tnc',
  },
};
